import { CANVAS_SIZE } from "@/lib/constants/slides";
import { toBlob } from "html-to-image";
import type { RefObject } from "react";

const SCREENSHOT_QUALITY = 0.85;

/**
 * Hook for capturing and uploading slide thumbnail screenshots.
 *
 * @param slideId - The unique identifier of the slide being screenshotted
 * @returns An object containing:
 *   - capturesSlideScreenShot: Async function that captures the canvas return the screenshot as a file
 *
 */
export function useSlideScreenshot(_slideId: string) {
  const capturesSlideScreenShot = async (canvasRef: RefObject<HTMLDivElement>) => {
    if (!canvasRef.current) return null;

    const canvas = canvasRef.current;

    const originalWidth = canvas.style.width;
    const originalHeight = canvas.style.height;
    const originalTransform = canvas.style.transform;

    const screenshotAspectRatio = CANVAS_SIZE.freeform.aspectRatio;
    const screenshotHeight = CANVAS_SIZE.freeform.size.height;
    const screenshotWidth = screenshotHeight * screenshotAspectRatio;

    try {
      // Set consistent screenshot dimensions for uniform thumbnails
      canvas.style.width = `${screenshotWidth}px`;
      canvas.style.height = `${screenshotHeight}px`;
      canvas.style.transform = "scale(1)";

      // Add class to hide selection indicators during screenshot
      canvas.classList.add("screenshot-mode");

      await new Promise((resolve) => setTimeout(resolve, 100));

      // capture screenshot
      const blob = await toBlob(canvas, {
        quality: SCREENSHOT_QUALITY,
        pixelRatio: 1,
        width: screenshotWidth,
        height: screenshotHeight,
        // Uploads CDN authorizes web clients via a signed cookie (see cloudfront-signing.service.ts);
        // fetch() omits cookies on cross-origin requests unless explicitly included.
        fetchRequestInit: { credentials: "include" },
        filter: (node: Node) => {
          // Skip video elements to prevent tainted canvas errors
          if (node instanceof HTMLVideoElement) {
            return false;
          }

          // Skip broken images (failed to load, 403, CORS errors, etc.)
          if (node instanceof HTMLImageElement) {
            if (!node.complete || node.naturalWidth === 0) {
              return false;
            }
          }

          // Filter out selection indicators for both HTML and SVG elements
          if (node instanceof HTMLElement || node instanceof SVGElement) {
            // Skip elements with data-screenshot-hide attribute
            if (node.hasAttribute("data-screenshot-hide")) {
              return false;
            }
            // Skip resize handles from react-rnd
            if (node.classList?.contains("react-resizable-handle")) {
              return false;
            }
            // Skip line handles
            if (node.classList?.contains("line-handle")) {
              return false;
            }
          }
          return true;
        },
      });

      return blob;
    } finally {
      // Restore original dimensions and remove screenshot mode
      canvas.style.width = originalWidth;
      canvas.style.height = originalHeight;
      canvas.style.transform = originalTransform;
      canvas.classList.remove("screenshot-mode");
    }
  };

  const captureScreenshotAsFile = async (canvasRef: RefObject<HTMLDivElement>, slideId: string) => {
    const blob = await capturesSlideScreenShot(canvasRef);
    if (!blob) return null;

    const file = new File([blob], `slide-${slideId}.jpg`, {
      type: "image/jpeg",
    });
    return file;
  };

  return {
    captureScreenshotAsFile,
  };
}
